import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Modal para conversión cuando no hay fondos en la moneda del gasto.
 * Permite editar el tipo de cambio (p. ej. el que aplicó el banco).
 *
 * exchangeRate = unidades de moneda destino (gasto) por 1 unidad de moneda origen.
 * Ej.: 1 USD = 3.37 PEN → exchangeRate = 3.37
 */
const InsufficientFundsConversionModal = ({
  open,
  onClose,
  onConfirm,
  expenseCurrency,
  expenseAmount,
  currentBalance,
  shortfall,
  selectedOption,
  formatDisplayAmount
}) => {
  const [rateInput, setRateInput] = useState("");

  useEffect(() => {
    if (open && selectedOption?.exchangeRate != null) {
      const r = selectedOption.exchangeRate;
      setRateInput(Number.isFinite(r) ? String(r) : "");
    }
  }, [open, selectedOption]);

  const parsedRate = useMemo(() => {
    const n = parseFloat(String(rateInput).replace(",", "."));
    return Number.isFinite(n) && n > 0 ? n : NaN;
  }, [rateInput]);

  const fromAmount = useMemo(() => {
    if (!Number.isFinite(parsedRate) || !expenseAmount) return NaN;
    return expenseAmount / parsedRate;
  }, [parsedRate, expenseAmount]);

  // Margen por redondeos (montos y tipos de cambio) para no bloquear el botón por centavos
  const AFFORD_EPS = 0.05;
  const canAfford =
    selectedOption &&
    Number.isFinite(fromAmount) &&
    fromAmount <= (selectedOption.available ?? 0) + AFFORD_EPS;

  const handleConfirm = () => {
    if (!selectedOption || !Number.isFinite(parsedRate) || !Number.isFinite(fromAmount)) return;
    if (!canAfford) return;
    onConfirm({
      exchangeRate: parsedRate,
      fromAmount
    });
  };

  if (!open || !selectedOption) return null;

  const fromLabel = selectedOption.label || selectedOption.code;

  const modal = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="insufficient-funds-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 id="insufficient-funds-title" className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Fondos insuficientes
        </h2>

        <div className="text-gray-600 dark:text-gray-300 text-sm space-y-2 mb-4">
          <p>
            No tienes suficiente balance en la moneda del gasto para cubrirlo de forma directa.
          </p>
          <p className="whitespace-pre-line">
            Balance disponible:{" "}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatDisplayAmount(currentBalance, false, expenseCurrency)}
            </span>
            {"\n"}
            Gasto a pagar:{" "}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatDisplayAmount(expenseAmount, false, expenseCurrency)}
            </span>
            {"\n"}
            Faltan:{" "}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatDisplayAmount(shortfall, false, expenseCurrency)}
            </span>
          </p>
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-200 mb-3">
          Puedes convertir desde <span className="font-semibold">{fromLabel}</span>. Ajusta el tipo de cambio si tu
          banco o casa de cambio aplicó otro valor:
        </p>

        <div className="mb-3">
          <label htmlFor="conversion-rate-input" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Tipo de cambio (1 {selectedOption.code} = ? {expenseCurrency})
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
              1 {selectedOption.code} =
            </span>
            <input
              id="conversion-rate-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              className="flex-1 min-w-[6rem] px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{expenseCurrency}</span>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3 mb-4 text-sm">
          <p className="text-gray-600 dark:text-gray-400 mb-1">Monto a convertir ({selectedOption.code})</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {Number.isFinite(fromAmount)
              ? formatDisplayAmount(fromAmount, false, selectedOption.code)
              : "—"}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Disponible en {selectedOption.code}:{" "}
            {formatDisplayAmount(selectedOption.available ?? 0, false, selectedOption.code)}
          </p>
        </div>

        {!Number.isFinite(parsedRate) && rateInput.trim() !== "" && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">Introduce un tipo de cambio válido (mayor que 0).</p>
        )}

        {Number.isFinite(fromAmount) && !canAfford && (
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            Con este tipo de cambio necesitas más {selectedOption.code} del que tienes disponible. Sube el tipo de
            cambio o cancela.
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!Number.isFinite(parsedRate) || !Number.isFinite(fromAmount) || !canAfford}
            className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
          >
            Sí, convertir y pagar
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined" || !document.body) return modal;
  return createPortal(modal, document.body);
};

export default InsufficientFundsConversionModal;
